import { lazy, Suspense } from "react";
import {
  ArrowLeft,
  Download,
  FileText,
  Loader2,
  Pencil,
  RotateCw,
  Star,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CategoryMark, StatusCell } from "@/components/invoices/InvoiceQuickChange";
import { InvoiceStatusBadge } from "@/components/invoices/InvoiceStatusBadge";
import { getInvoiceCategory } from "@/lib/invoiceCategories";
import { PROCESSING_STATUS } from "@/lib/statusEnums";
import { formatCurrency, formatInvoiceDate, formatValueOrEmpty } from "@/lib/invoiceStatus";
import { getActionButtonClass } from "@/lib/semanticColors";
import { cn } from "@/lib/utils";

// Kept out of the main bundle — the viewer is only needed on this route.
const InvoicePdfViewer = lazy(() =>
  import("@/components/invoices/detail/InvoicePdfViewer"),
);

const iconAction = (action) =>
  cn("size-8 rounded-full shadow-none", getActionButtonClass(action));

/**
 * Identity block at the top of the work panel: which invoice this is, what state
 * it is in, and the actions that apply to the record as a whole.
 *
 * This used to be a full-width band above both columns, which cost about 105px
 * of height across the whole page so that the PDF — the thing being read — got
 * less of it. Sitting inside the panel, it costs the same rows but takes them
 * from the column that has text to spare.
 */
export function InvoiceRecordHeader({
  detail,
  viewOnly,
  onBack,
  onToggleStar,
  isTogglingStar,
  onExtract,
  isExtracting,
  canExtract = false,
  canEdit = false,
  // Opening the type and status pickers; left out where the user may not change them.
  onChangeCategory,
  onChangeStatus,
}) {
  const navigate = useNavigate();
  const categoryName =
    detail.categoryName ?? getInvoiceCategory(detail.categoryId)?.name ?? null;

  // The old app printed the category as a named tag here. The dot beside the
  // invoice number is the colour anchor; the name belongs in the facts line,
  // where it is readable without hovering and reaches a screen reader.
  const facts = [
    detail.companyName,
    categoryName,
    detail.totalAmount != null
      ? formatCurrency(detail.totalAmount, detail.currencySymbol)
      : null,
    detail.invoiceDate ? formatInvoiceDate(detail.invoiceDate) : null,
  ].filter(Boolean);

  return (
    <header className="invoice-record-head">
      <div className="invoice-record-head-row">
        <div className="invoice-record-identity">
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={onBack ?? (() => navigate(-1))}
                  aria-label="Back to the invoice list"
                  className={cn("-ms-1 shrink-0", iconAction("back"))}
                />
              }
            >
              <ArrowLeft className="size-4" />
            </TooltipTrigger>
            <TooltipContent>Back to the invoice list</TooltipContent>
          </Tooltip>

          <CategoryMark invoice={detail} onSelect={onChangeCategory} />
          <h2 className="invoice-record-number" title={detail.invoiceNumber ?? undefined}>
            {formatValueOrEmpty(detail.invoiceNumber)}
          </h2>
          {onChangeStatus ? (
            <>
              {/* The "auto" badge shows the review status while an invoice is under
                  review; the picker changes the processing status, so when it can
                  be opened both are shown, the review one as a plain badge. */}
              {detail.processingStatusId === PROCESSING_STATUS.UNDER_REVIEW && (
                <InvoiceStatusBadge invoice={detail} kind="review" compact />
              )}
              <StatusCell invoice={detail} kind="processing" onSelect={onChangeStatus} />
            </>
          ) : (
            <InvoiceStatusBadge invoice={detail} compact />
          )}
        </div>

        <div className="invoice-record-actions">
          {canExtract && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    onClick={onExtract}
                    disabled={isExtracting}
                    aria-label="Rerun data extraction"
                    className={iconAction("rerun")}
                  />
                }
              >
                <RotateCw className={cn("size-3.5", isExtracting && "animate-spin")} />
              </TooltipTrigger>
              <TooltipContent>
                {isExtracting ? "Rerunning extraction…" : "Rerun data extraction"}
              </TooltipContent>
            </Tooltip>
          )}

          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  onClick={onToggleStar}
                  disabled={isTogglingStar}
                  aria-pressed={detail.starred}
                  aria-label={detail.starred ? "Remove from starred" : "Add to starred"}
                  className={iconAction("star")}
                />
              }
            >
              <Star className={cn("size-3.5", detail.starred && "fill-current")} />
            </TooltipTrigger>
            <TooltipContent>{detail.starred ? "Starred" : "Add to starred"}</TooltipContent>
          </Tooltip>

          {/* TEMP: status lock removed, only the permission gates Edit for now.
              TODO: restore `viewOnly && canEdit && !getEditLockReason(detail)`
              (import from "@/lib/invoiceActions"), or disable it with the
              reason as a tooltip. */}
          {viewOnly && canEdit && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    nativeButton={false}
                    render={<Link to={`/invoice/edit/${detail.id}`} />}
                    aria-label="Edit this invoice"
                    className={iconAction("edit")}
                  />
                }
              >
                <Pencil className="size-3.5" />
              </TooltipTrigger>
              <TooltipContent>Edit this invoice</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {facts.length > 0 && (
        <p className="invoice-record-meta">
          {facts.map((fact, index) => (
            <span key={`${fact}-${index}`}>
              {index > 0 && (
                <span className="invoice-record-meta-sep" aria-hidden="true">
                  ·
                </span>
              )}
              {fact}
            </span>
          ))}
        </p>
      )}
    </header>
  );
}

/**
 * The document itself: its name, the way to save it, and the viewer.
 *
 * Split out from the panel wrapper because the document appears in two places
 * now — its own column when the page is wide enough to split, and a tab in the
 * single-panel layout when it is not. Only the chrome around it differs.
 */
export function InvoicePdfStage({ fileName, file, onDownload }) {
  return (
    <>
      <div className="invoice-detail-pdf-toolbar">
        <div className="flex min-w-0 items-center gap-2">
          <FileText className="size-4 shrink-0 text-primary" aria-hidden="true" />
          <p className="truncate text-sm font-medium text-foreground" title={fileName}>
            {fileName}
          </p>
        </div>
        {onDownload && (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  disabled={!file?.blob}
                  onClick={onDownload}
                  aria-label="Download the supplier PDF"
                  className={iconAction("download")}
                />
              }
            >
              <Download className="size-3.5" />
            </TooltipTrigger>
            <TooltipContent>Download the supplier PDF</TooltipContent>
          </Tooltip>
        )}
      </div>

      <div className="invoice-detail-pdf-stage">
        <Suspense
          fallback={
            <div
              role="status"
              className="flex flex-1 items-center justify-center gap-2 p-8 text-sm text-muted-foreground"
            >
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              Loading viewer…
            </div>
          }
        >
          <InvoicePdfViewer
            blob={file?.blob}
            isLoading={file?.isLoading}
            error={file?.error}
          />
        </Suspense>
      </div>
    </>
  );
}

/**
 * The document column. Nothing between the toolbar and the viewer — the PDF is
 * the evidence the operator is checking the form against, so it takes the height.
 */
export function InvoicePdfPanel({ fileName, file, onDownload }) {
  return (
    <div className="page-panel-wrap invoice-detail-pdf-wrap">
      <section className="invoice-detail-pdf page-panel">
        <InvoicePdfStage fileName={fileName} file={file} onDownload={onDownload} />
      </section>
    </div>
  );
}
